/**
 * Campaigns Management Page
 * View and manage all email marketing campaigns
 */

import React, { useState, useEffect } from 'react';
import {
  getCampaigns,
  deleteCampaign,
  duplicateCampaign,
  AUDIENCES,
  type Campaign,
} from '../services/campaigns';
import '../styles/pages/campaigns-management.scss';

interface CampaignsManagementProps {
  onNavigate?: (page: 'campaignComposer', campaignId?: string) => void;
}

const CampaignsManagement: React.FC<CampaignsManagementProps> = ({ onNavigate }) => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'draft' | 'sent' | 'scheduled'>(
    'all'
  );

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const data = await getCampaigns();
      setCampaigns(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (
      !window.confirm(`Are you sure you want to delete "${name}"? This cannot be undone.`)
    ) {
      return;
    }

    try {
      await deleteCampaign(id);
      setCampaigns((prev) => prev.filter((c) => c.id !== id));
    } catch (err: any) {
      setError(err.message || 'Failed to delete campaign');
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      const duplicated = await duplicateCampaign(id);
      setCampaigns((prev) => [duplicated, ...prev]);
    } catch (err: any) {
      setError(err.message || 'Failed to duplicate campaign');
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      draft: 'badge--secondary',
      sent: 'badge--success',
      scheduled: 'badge--warning',
      failed: 'badge--error',
    };
    return badges[status as keyof typeof badges] || '';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredCampaigns = campaigns.filter((campaign) => {
    if (filter === 'all') return true;
    return campaign.status === filter;
  });

  if (loading) {
    return (
      <div className="campaigns-management">
        <div className="campaigns-management__loading">
          <p>Loading campaigns...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="campaigns-management">
      <div className="campaigns-management__header">
        <div>
          <h1>Email Campaigns</h1>
          <p className="campaigns-management__subtitle">
            Create and manage email marketing campaigns
          </p>
        </div>
        <button
          onClick={() => onNavigate?.('campaignComposer')}
          className="btn btn--primary"
        >
          Create Campaign
        </button>
      </div>

      {error && (
        <div className="alert alert--error" role="alert">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="campaigns-management__filters">
        <button
          className={`campaigns-management__filter ${
            filter === 'all' ? 'campaigns-management__filter--active' : ''
          }`}
          onClick={() => setFilter('all')}
        >
          All ({campaigns.length})
        </button>
        <button
          className={`campaigns-management__filter ${
            filter === 'draft' ? 'campaigns-management__filter--active' : ''
          }`}
          onClick={() => setFilter('draft')}
        >
          Drafts ({campaigns.filter((c) => c.status === 'draft').length})
        </button>
        <button
          className={`campaigns-management__filter ${
            filter === 'sent' ? 'campaigns-management__filter--active' : ''
          }`}
          onClick={() => setFilter('sent')}
        >
          Sent ({campaigns.filter((c) => c.status === 'sent').length})
        </button>
        <button
          className={`campaigns-management__filter ${
            filter === 'scheduled' ? 'campaigns-management__filter--active' : ''
          }`}
          onClick={() => setFilter('scheduled')}
        >
          Scheduled ({campaigns.filter((c) => c.status === 'scheduled').length})
        </button>
      </div>

      {/* Campaigns List */}
      {filteredCampaigns.length === 0 ? (
        <div className="card campaigns-management__empty">
          <p>No campaigns found.</p>
          <button
            onClick={() => onNavigate?.('campaignComposer')}
            className="btn btn--primary"
          >
            Create Your First Campaign
          </button>
        </div>
      ) : (
        <div className="campaigns-management__list">
          {filteredCampaigns.map((campaign) => (
            <div key={campaign.id} className="card campaigns-management__item">
              <div className="campaigns-management__item-header">
                <div>
                  <h3>
                    <button
                      onClick={() => onNavigate?.('campaignComposer', campaign.id)}
                      className="campaigns-management__title-link"
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'inherit',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        padding: 0,
                        font: 'inherit',
                      }}
                    >
                      {campaign.name}
                    </button>
                  </h3>
                  <p className="campaigns-management__subject">
                    {campaign.subject}
                  </p>
                </div>
                <span className={`badge ${getStatusBadge(campaign.status)}`}>
                  {campaign.status.charAt(0).toUpperCase() +
                    campaign.status.slice(1)}
                </span>
              </div>

              <div className="campaigns-management__item-details">
                <div className="campaigns-management__detail">
                  <strong>Template:</strong>{' '}
                  {AUDIENCES[campaign.template_type]?.name || campaign.template_type}
                </div>
                <div className="campaigns-management__detail">
                  <strong>Audiences:</strong>{' '}
                  {campaign.audience_ids
                    .map((id) => AUDIENCES[id as keyof typeof AUDIENCES]?.name || id)
                    .join(', ')}
                </div>
                {campaign.sent_at && (
                  <div className="campaigns-management__detail">
                    <strong>Sent:</strong> {formatDate(campaign.sent_at)}
                  </div>
                )}
                {campaign.recipient_count > 0 && (
                  <div className="campaigns-management__detail">
                    <strong>Recipients:</strong> {campaign.recipient_count}
                  </div>
                )}
                {campaign.error_message && (
                  <div className="campaigns-management__detail campaigns-management__detail--error">
                    <strong>Error:</strong> {campaign.error_message}
                  </div>
                )}
              </div>

              <div className="campaigns-management__item-actions">
                {campaign.status === 'draft' && (
                  <button
                    onClick={() => onNavigate?.('campaignComposer', campaign.id)}
                    className="btn btn--sm btn--outline"
                  >
                    Edit
                  </button>
                )}
                <button
                  className="btn btn--sm btn--outline"
                  onClick={() => handleDuplicate(campaign.id)}
                >
                  Duplicate
                </button>
                {campaign.status === 'draft' && (
                  <button
                    className="btn btn--sm btn--outline btn--danger"
                    onClick={() => handleDelete(campaign.id, campaign.name)}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CampaignsManagement;
